import { Assert } from '@cotera/utilities';
import { evaluateWidth, FormatNode } from './Node';
import { INDENT_WIDTH, MAX_LINE_WIDTH } from './formatter';
import _ from 'lodash';

/**
 * Renders `FormatNode`s into formatted EraQL strings.
 */
class Generator {
  /**
   * The buffer where the final formatted output code is collected.
   */
  private buffer: string = '';

  /**
   * The indentation level of the current line.
   */
  private indentLevel: number = 0;

  /**
   * The "width" of the current line.
   *
   * i.e. The number of "extended grapheme clusters" (or approximate user-facing character width) on the current line.
   */
  private currLineWidth: number = 0;

  /**
   * The set of all "head" nodes whose "dependents" (non-head descendents) need to be wrapped.
   */
  private wrappedHeadIds: Set<string> = new Set();

  /**
   * Invokes generateSubProgram on the root formatting Node and returns
   * the final formatted program after traversing the Node tree.
   *
   * Warning: this method is only meant to be called once.
   */
  generateProgram(root: FormatNode): string {
    this.generateSubProgram(root, 'Detect');

    return this.buffer;
  }

  /**
   * Take a `Node`, render it to a formatted `string`, and add it to the `buffer`.
   *
   * If the nearest parent "head" node needs wrapping (ie cannot if on one line),
   * then wraps all dependents (non-head descendents).
   *
   * @param wrap Whether the nearest parent "head" node needs wrapping.
   */
  private generateSubProgram(node: FormatNode, wrap: Wrap): void {
    switch (node.t) {
      case 'nodes':
        node.nodes.forEach((child) => this.generateSubProgram(child, wrap));
        return;

      case 'head': {
        const { nodes } = node;

        const groupWidth = _.sum(
          nodes.map((child) => evaluateWidth(child, this.wrappedHeadIds))
        );

        const isWrapGroup = this.currLineWidth + groupWidth > MAX_LINE_WIDTH;

        if (isWrapGroup) {
          this.wrappedHeadIds.add(node.headId);
        }

        const childrenWrap: Wrap = isWrapGroup ? 'Enable' : 'Detect';

        nodes.forEach((child) => this.generateSubProgram(child, childrenWrap));

        return;
      }

      case 'ifWrapAncestor': {
        const { ancestorHeadId: ancestorGroupId, A, B } = node;

        this.wrappedHeadIds.has(ancestorGroupId)
          ? this.generateSubProgram(A, 'Enable') // Todo, add a spec test that fails if `wrap` is passed instead of 'Enable`
          : this.generateSubProgram(B, wrap);

        return;
      }

      case 'text':
        this.generateText(node.value, node.width);
        return;

      case 'line':
        this.generateNewLine();
        return;

      case 'lineOrNothing':
        if (wrap === 'Enable') {
          this.generateNewLine();
        }
        return;

      case 'lineOrSpace':
        wrap === 'Enable' ? this.generateNewLine() : this.generateText(' ', 1);
        return;

      case 'indent': {
        const { nodes } = node;

        if (wrap === 'Enable') {
          this.indentLevel += 1;
          // TODO Push indent (and size) here?
          nodes.map((child) => this.generateSubProgram(child, wrap));
          this.indentLevel -= 1;
          //
        } else {
          nodes.map((child) => this.generateSubProgram(child, wrap));
        }
        return;
      }

      default:
        return Assert.unreachable(node);
    }
  }

  /**
   * Adds `value` to the bufer.
   */
  private generateText(value: string, chars: number) {
    this.currLineWidth += chars;
    this.buffer += value;
  }

  /**
   * Adds a new line to the buffer (while ensuring the new line starts off
   * with the proper indentation).
   */
  private generateNewLine() {
    this.currLineWidth = INDENT_WIDTH * this.indentLevel;

    const indent = ' '.repeat(INDENT_WIDTH).repeat(this.indentLevel);

    this.buffer += `\n${indent}`;
  }
}

type Wrap = 'Enable' | 'Detect';

export const generateProgram = (root: FormatNode): string =>
  new Generator().generateProgram(root);
